 import React, { ReactNode, useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import { ColumnDef } from "@tanstack/react-table"
import useDataTable from "hooks/useDataTable"
import withRouter from "Components/Common/withRouter"
import DepartmentDetailTemplate from "Components/templates/deprtment-detail"
import DataTable from "Components/molecule/data-table"
import Modal from "Components/molecule/modal"
import AreYouSureModal from "Components/organisms/are-you-sure-modal"
import DepartmentMappingForm from "Components/organisms/forms/department-mapping-form"
import DepartmentMappingFields from "Components/organisms/forms/department-mapping-form/types"
import DepartmentPermissionForm from "Components/organisms/forms/department-permission-form"
import DepartmentPermissionFields from "Components/organisms/forms/department-permission-form/types"
import { SelectOption } from "Components/molecule/inputs/select-input"
import SearchSelect from "Components/molecule/inputs/search-select"
import ActionButton from "Components/atoms/buttons/action-button"
import IconButton from "Components/atoms/buttons/icon-button"
import { DepartmentMappingFormValidation } from "data/department-mapping-form"
import { DepartmentPermissionFormValidation } from "data/department-permission-form"
import useAreYouSureModal from "hooks/useAreYouSureModal"
import Department from "models/department"
import Employee from "models/employee"
import DepartmentKPI from "models/department-kpi"
import FiscalYear from "models/fiscal-year"
import DepartmentMapping, { ExternalSystem } from "models/department-mapping"
import EmployeeDepartmentPermission, { PermissionTypeName } from "models/employee-department-permission"
import Branch from "models/branch"
import {
    getDepartment,
    getDepartmentFormFields,
    getDepartmentPermissions,
    grantDepartmentPermission,
    revokeDepartmentPermission,
    getDepartmentMappings,
    addDepartmentMappingRequest,
    updateDepartmentMappingRequest,
    removeDepartmentMappingRequest,
    getMappingSources,
    getDepartmentKPIs,
    getDepartmentKPIFormFields,
    deleteDepartmentKPIRequest,
    deleteDepartmentRequest
} from "slices/admin/departments/thunk"
import { removeDepartment } from "slices/admin/departments/reducer"
import { useDispatch } from "react-redux"
import Tabs from "Components/molecule/tabs"
import TextInput from "Components/molecule/inputs/text-input"
import {
    navigateToDepartments,
    navigateToEditDepartment,
    navigateToCreateDepartmentKPI,
    navigateToEditDepartmentKPI,
    navigateToDepartmentKPIs
} from "utils/navigator"
import EmployeeUtility from "utils/models/employee-utility"
import { formatCurrency } from "utils/tools"
import { PlusIcon, PencilIcon, TrashIcon, ExportIcon } from "Components/atoms/icons"
import useManual from "hooks/useManual"


const GRANTABLE_TYPES = [PermissionTypeName.Admin, PermissionTypeName.Edit, PermissionTypeName.Read]

// Last column with the row's buttons, always together (edit next to delete)
const actionsColumn = <T,>(render: (row: T) => ReactNode): ColumnDef<T> => ({
    header: "",
    id: "actions",
    size: 90,
    enableSorting: false,
    enableHiding: false,
    meta: { cellClassName: "text-end" },
    cell: ({ row }) => <div className="d-flex justify-content-end gap-1">{render(row.original)}</div>,
})

const DepartmentDetailsPage = (props: any) => {
    document.title = "Department Details | IbexIQ";
    const { handleOpenManual } = useManual("admin/departments");
    const [searchParams] = useSearchParams()
    const departmentId = searchParams.get("departmentId")

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [department, setDepartment] = useState<Department | null>(null)
    const [employees, setEmployees] = useState<Employee[]>([])

    const [permissions, setPermissions] = useState<EmployeeDepartmentPermission[]>([])
    const [kpis, setKPIs] = useState<DepartmentKPI[]>([])
    const [kpiBranches, setKPIBranches] = useState<Branch[]>([])
    const [kpiFiscalYears, setKPIFiscalYears] = useState<FiscalYear[]>([])
    const [kpiBranchFilter, setKPIBranchFilter] = useState<string>('')
    const [kpiFiscalYearFilter, setKPIFiscalYearFilter] = useState<string>('')
    const [mappings, setMappings] = useState<DepartmentMapping[]>([])
    const [mappingSources, setMappingSources] = useState<ExternalSystem[]>([])

    const [focusedMapping, setFocusedMapping] = useState<DepartmentMapping | null>(null)
    const [showMappingModal, setShowMappingModal] = useState<boolean>(false)
    const [mappingFilter, setMappingFilter] = useState<string>('')
    const [mappingSourceFilter, setMappingSourceFilter] = useState<string>('')
    const [accessFilter, setAccessFilter] = useState<string>('')
    const [activeTab, setActiveTab] = useState<string>('KPIs')
    const [showPermissionModal, setShowPermissionModal] = useState<boolean>(false)

    const confirmModal = useAreYouSureModal()
    const dispatch: any = useDispatch()

    const canManage = !!department?.isGlobalAdmin
        || department?.yourPermission === PermissionTypeName.Owner
        || department?.yourPermission === PermissionTypeName.Admin

    const initializePage = async () => {
        if (!departmentId) {
            toast.error("Department ID not found")
            props.router.navigate(-1)
            return
        }

        const [dept, fields] = await Promise.all([
            getDepartment(departmentId),
            getDepartmentFormFields()
        ])

        if (!dept) {
            toast.error("Could not find department")
            props.router.navigate(-1)
            return
        }

        if (!dept.isGlobalAdmin && !dept.yourPermission) {
            toast.error("You do not have permission to view this department")
            props.router.navigate(-1)
            return
        }

        setDepartment(dept)
        if (fields) {
            setEmployees(fields.employees)
        }

        // KPIs are readable by anyone who can view the department, unlike mappings and
        // permissions below.
        const [depKPIs, kpiFields] = await Promise.all([
            getDepartmentKPIs(departmentId),
            getDepartmentKPIFormFields()
        ])
        if (depKPIs) setKPIs(depKPIs)
        if (kpiFields) {
            setKPIBranches(kpiFields.branches)
            setKPIFiscalYears(kpiFields.fiscalYears)
        }

        if (dept.isGlobalAdmin
            || dept.yourPermission === PermissionTypeName.Owner
            || dept.yourPermission === PermissionTypeName.Admin) {
            const [perms, maps, sources] = await Promise.all([
                getDepartmentPermissions(departmentId),
                getDepartmentMappings(departmentId),
                getMappingSources()
            ])
            if (perms) setPermissions(perms)
            if (maps) setMappings(maps)
            if (sources) setMappingSources(sources)
        }

        setIsLoading(false)
    }

    useEffect(() => {
        initializePage()
    }, [departmentId])

    const handleBack = () => navigateToDepartments(props.router.navigate)

    const handleEdit = () => {
        if (!departmentId) return
        navigateToEditDepartment(props.router.navigate, departmentId)
    }

    // Delete lives here (not in the departments list): owners and global admins only
    const canDelete = !!department?.isGlobalAdmin || department?.yourPermission === PermissionTypeName.Owner
    const handleDelete = () => {
        if (!department) return
        confirmModal.open({
            title: "Delete Department",
            subTitle: `Delete ${department.code} — ${department.description}?`,
            acceptText: "Delete",
            processingText: "Deleting...",
            action: async () => {
                const success = await deleteDepartmentRequest(department.id)
                if (success) {
                    dispatch(removeDepartment(department.id))
                    navigateToDepartments(props.router.navigate)
                }
                return success
            }
        })
    }

    const employeeOptions: SelectOption[] = useMemo(() => employees.map(e => ({
        value: e.id, label: EmployeeUtility.fullName(e)
    })), [employees])

    const grantTypeOptions: SelectOption[] = GRANTABLE_TYPES.map(t => ({ value: t, label: t }))
    const sourceOptions: SelectOption[] = useMemo(() => mappingSources.map(s => ({
        value: s.id, label: s.name
    })), [mappingSources])

    const onSubmitPermission = async (vals: DepartmentPermissionFields, { resetForm }: any) => {
        if (!departmentId) return

        const employee = employees.find(e => e.id === vals.employeeId)
        const employeeName = EmployeeUtility.fullName(employee)

        confirmModal.open({
            title: "Grant Permission",
            subTitle: `Give ${employeeName} ${vals.permissionTypeName} access to this department?`,
            acceptText: "Grant",
            processingText: "Granting...",
            action: async () => {
                const res = await grantDepartmentPermission(departmentId, vals.employeeId, vals.permissionTypeName)
                if (res) {
                    setPermissions(prev => {
                        const filtered = prev.filter(p => p.employeeId !== vals.employeeId)
                        return [res, ...filtered]
                    })
                    resetForm()
                    setShowPermissionModal(false)
                }
                return res
            }
        })
    }

    const permissionFormik = useFormik({
        initialValues: {
            employeeId: '',
            permissionTypeName: ''
        } as DepartmentPermissionFields,
        onSubmit: onSubmitPermission,
        validationSchema: DepartmentPermissionFormValidation
    })

    const handleOpenPermissionModal = () => setShowPermissionModal(true)

    const handleClosePermissionModal = () => {
        setShowPermissionModal(false)
        permissionFormik.resetForm()
    }

    const handleRevokePermission = (permission: EmployeeDepartmentPermission) => {
        const employeeName = EmployeeUtility.fullName(permission.employee)

        confirmModal.open({
            title: "Revoke Permission",
            subTitle: `Remove ${employeeName}'s access to this department?`,
            acceptText: "Revoke",
            processingText: "Revoking...",
            action: async () => {
                if (!departmentId) return false
                const success = await revokeDepartmentPermission(departmentId, permission.employeeId)
                if (success) {
                    setPermissions(prev => prev.filter(p => p.employeeId !== permission.employeeId))
                }
                return success
            }
        })
    }

    const onSubmitMapping = async (vals: DepartmentMappingFields, { resetForm }: any) => {
        if (!departmentId) return

        const editing = focusedMapping

        confirmModal.open({
            title: editing ? "Update Mapping" : "Add Mapping",
            subTitle: editing
                ? `Change the external code from ${editing.id} to ${vals.id}?`
                : `Map ${vals.id} to this department?`,
            acceptText: editing ? "Update" : "Add",
            processingText: "Saving...",
            action: async () => {
                if (editing) {
                    const res = await updateDepartmentMappingRequest(departmentId, editing.id, vals.id)
                    if (res) {
                        setMappings(prev => prev.map(m => m.id === editing.id ? { ...m, ...res } : m))
                        setFocusedMapping(null)
                        setShowMappingModal(false)
                        resetForm()
                    }
                    return res
                }

                const res = await addDepartmentMappingRequest(departmentId, vals.id, vals.sourceTypeId)
                if (res) {
                    const sourceType = mappingSources.find(s => s.id === vals.sourceTypeId)
                    setMappings(prev => [{ ...res, sourceType }, ...prev])
                    setShowMappingModal(false)
                    resetForm()
                }
                return res
            }
        })
    }

    const mappingFormik = useFormik({
        initialValues: {
            id: focusedMapping?.id ?? '',
            sourceTypeId: focusedMapping?.sourceTypeId ?? ''
        } as DepartmentMappingFields,
        onSubmit: onSubmitMapping,
        enableReinitialize: true,
        validationSchema: DepartmentMappingFormValidation
    })

    const handleEditMapping = (mapping: DepartmentMapping) => {
        setFocusedMapping(mapping)
        setShowMappingModal(true)
    }

    const handleAddMapping = () => {
        setFocusedMapping(null)
        setShowMappingModal(true)
    }

    const handleCancelMappingEdit = () => {
        setFocusedMapping(null)
        setShowMappingModal(false)
        mappingFormik.resetForm()
    }

    const handleRemoveMapping = (mapping: DepartmentMapping) => {
        confirmModal.open({
            title: "Delete Mapping",
            subTitle: `Remove the mapping for ${mapping.id}?`,
            acceptText: "Delete",
            processingText: "Deleting...",
            action: async () => {
                if (!departmentId) return false
                const success = await removeDepartmentMappingRequest(departmentId, mapping.id)
                if (success) {
                    setMappings(prev => prev.filter(m => m.id !== mapping.id))
                }
                return success
            }
        })
    }

    const mappingColumns = React.useMemo<ColumnDef<DepartmentMapping>[]>(() => [
        {
            header: "External Code",
            accessorKey: "id",
            size: 150,
        },
        {
            header: "Source System",
            id: "sourceType",
            size: 150,
            accessorFn: (m) => m.sourceType?.name,
            cell: ({ row }) => row.original.sourceType?.name ?? "—"
        },
        actionsColumn<DepartmentMapping>(m => <>
            <IconButton title="Edit" onClick={() => handleEditMapping(m)}><PencilIcon /></IconButton>
            <IconButton title="Delete" onClick={() => handleRemoveMapping(m)}><TrashIcon /></IconButton>
        </>)
    ], [])

    const filteredMappings = useMemo(() => mappings.filter(m =>
        m.id.toLowerCase().includes(mappingFilter.trim().toLowerCase())
        && (!mappingSourceFilter || m.sourceTypeId === mappingSourceFilter)
    ), [mappings, mappingFilter, mappingSourceFilter])

    const mappingsTable = useDataTable({
        tableId: 'department-mappings',
        columns: mappingColumns,
        data: filteredMappings,
        initialSorting: [{ id: 'id', desc: false }],
        columnResizeMode: 'onEnd',
        enableColumnResizing: true
    })

    const handleOpenKPIModule = () => navigateToDepartmentKPIs(props.router.navigate, departmentId ?? undefined)

    const handleAddKPI = () => {
        if (!departmentId) return
        navigateToCreateDepartmentKPI(props.router.navigate, departmentId)
    }

    const handleEditKPI = (kpi: DepartmentKPI) => {
        if (!departmentId) return
        navigateToEditDepartmentKPI(props.router.navigate, departmentId, kpi.branchId, kpi.fiscalYear)
    }

    const handleRemoveKPI = (kpi: DepartmentKPI) => {
        confirmModal.open({
            title: "Delete KPIs",
            subTitle: `Remove the ${kpi.fiscalYear} KPIs for this department?`,
            acceptText: "Delete",
            processingText: "Deleting...",
            action: async () => {
                if (!departmentId) return false
                const success = await deleteDepartmentKPIRequest(departmentId, kpi.branchId, kpi.fiscalYear)
                if (success) {
                    setKPIs(prev => prev.filter(k => !(k.branchId === kpi.branchId && k.fiscalYear === kpi.fiscalYear)))
                }
                return success
            }
        })
    }

    const kpiColumns = React.useMemo<ColumnDef<DepartmentKPI>[]>(() => [
        {
            header: "Fiscal Year",
            accessorKey: "fiscalYear",
            size: 120,
        },
        { header: "Branch", size: 130, accessorFn: (k) => k.branch?.name, cell: ({ row }) => row.original.branch?.name ?? "—" },
        { header: "Budget", size: 120, accessorFn: (k) => Number(k.budget), cell: ({ row }) => formatCurrency(row.original.budget) },
        { header: "Annual Revenue", size: 130, accessorFn: (k) => Number(k.annualRevenue), cell: ({ row }) => formatCurrency(row.original.annualRevenue) },
        { header: "Gross Margin", size: 120, accessorFn: (k) => Number(k.grossMargin), cell: ({ row }) => formatCurrency(row.original.grossMargin) },
        { header: "Work In Hand", size: 120, accessorFn: (k) => Number(k.workInHand), cell: ({ row }) => formatCurrency(row.original.workInHand) },
        { header: "Blue Zone", size: 110, accessorFn: (k) => Number(k.blueZone), cell: ({ row }) => formatCurrency(row.original.blueZone) },
        { header: "Yellow Zone", size: 110, accessorFn: (k) => Number(k.yellowZone), cell: ({ row }) => formatCurrency(row.original.yellowZone) },
        { header: "Opportunity Win Total", size: 150, accessorFn: (k) => Number(k.opportunityWinTotal), cell: ({ row }) => formatCurrency(row.original.opportunityWinTotal) },
        { header: "Project Profitability", size: 140, accessorFn: (k) => Number(k.projectProfitability), cell: ({ row }) => formatCurrency(row.original.projectProfitability) },
        {
            header: "Performance Management",
            accessorKey: "performanceManagement",
            size: 200,
            cell: ({ row }) => row.original.performanceManagement ?? "—"
        },
        ...(canManage ? [actionsColumn<DepartmentKPI>(k => <>
            <IconButton title="Edit" onClick={() => handleEditKPI(k)}><PencilIcon /></IconButton>
            <IconButton title="Delete" onClick={() => handleRemoveKPI(k)}><TrashIcon /></IconButton>
        </>)] : [])
    ], [canManage])

    const kpiBranchOptions: SelectOption[] = useMemo(() => kpiBranches.map(b => ({
        value: b.id, label: b.name
    })), [kpiBranches])

    // Visible years, plus any year this department already has KPIs for (even if hidden) so the filter can find them.
    const kpiFiscalYearOptions: SelectOption[] = useMemo(() => {
        const years = new Set<number>(kpiFiscalYears.map(f => f.id))
        kpis.forEach(k => years.add(k.fiscalYear))
        return Array.from(years).sort((a, b) => b - a).map(y => ({ value: String(y), label: String(y) }))
    }, [kpiFiscalYears, kpis])

    const filteredKPIs = useMemo(() => kpis.filter(k =>
        (!kpiBranchFilter || k.branchId === kpiBranchFilter)
        && (!kpiFiscalYearFilter || k.fiscalYear === Number(kpiFiscalYearFilter))
    ), [kpis, kpiBranchFilter, kpiFiscalYearFilter])

    const kpisTable = useDataTable({
        tableId: 'department-kpis',
        columns: kpiColumns,
        data: filteredKPIs,
        columnResizeMode: 'onEnd',
        enableColumnResizing: true
    })

    const permissionColumns = React.useMemo<ColumnDef<EmployeeDepartmentPermission>[]>(() => [
        {
            header: "Employee",
            id: "employee",
            size: 200,
            cell: ({ row }) => EmployeeUtility.fullName(row.original.employee),
        },
        {
            header: "Permission",
            id: "permissionType",
            size: 100,
            accessorFn: (p) => p.permissionType?.name,
            cell: ({ row }) => row.original.permissionType?.name ?? "—"
        },
        // Owners can't be revoked
        actionsColumn<EmployeeDepartmentPermission>(p => p.permissionType?.name !== PermissionTypeName.Owner
            ? <IconButton title="Revoke" onClick={() => handleRevokePermission(p)}><TrashIcon /></IconButton>
            : null)
    ], [])

    const filteredPermissions = useMemo(() => permissions.filter(p =>
        EmployeeUtility.fullName(p.employee).toLowerCase().includes(accessFilter.trim().toLowerCase())
    ), [permissions, accessFilter])

    const permissionsTable = useDataTable({
        tableId: 'department-access',
        columns: permissionColumns,
        data: filteredPermissions,
        initialSorting: [{ id: 'employee', desc: false }],
        columnResizeMode: 'onEnd',
        enableColumnResizing: true
    })

    // Each tab: title, then filters + actions on top, then the table (no inner scroll)
    const tabs: Record<string, ReactNode> = {
        KPIs: <div className="department-tab">
            <div className="department-tab__toolbar">
                <SearchSelect
                    label={"Branch"}
                    placeholder={"All branches"}
                    value={kpiBranchFilter || null}
                    options={kpiBranchOptions}
                    clearable
                    onChange={(option) => setKPIBranchFilter(option ? option.value : '')}
                />
                <SearchSelect
                    label={"Fiscal Year"}
                    placeholder={"All fiscal years"}
                    value={kpiFiscalYearFilter || null}
                    options={kpiFiscalYearOptions}
                    clearable
                    onChange={(option) => setKPIFiscalYearFilter(option ? option.value : '')}
                />
                <div className="department-tab__actions">
                    {canManage && <ActionButton onClick={handleAddKPI}>Add KPIs <PlusIcon /></ActionButton>}
                    <ActionButton onClick={handleOpenKPIModule}>All Department KPIs <ExportIcon /></ActionButton>
                </div>
            </div>
            <DataTable table={kpisTable} columns={kpiColumns} />
        </div>,
    }

    if (canManage) {
        tabs["External Mapping"] = <div className="department-tab">
            <div className="department-tab__toolbar">
                <TextInput label="External Code" placeholder="Search code" value={mappingFilter} onChange={(e) => setMappingFilter(e.target.value)} />
                <SearchSelect
                    label="Source System"
                    placeholder="All systems"
                    value={mappingSourceFilter || null}
                    options={sourceOptions}
                    clearable
                    onChange={(option) => setMappingSourceFilter(option ? option.value : '')}
                />
                <div className="department-tab__actions">
                    <ActionButton onClick={handleAddMapping}>Add Mapping <PlusIcon /></ActionButton>
                </div>
            </div>
            <DataTable table={mappingsTable} columns={mappingColumns} />
        </div>

        tabs["Access Control"] = <div className="department-tab">
            <div className="department-tab__toolbar">
                <TextInput label="Employee" placeholder="Search name" value={accessFilter} onChange={(e) => setAccessFilter(e.target.value)} />
                <div className="department-tab__actions">
                    <ActionButton onClick={handleOpenPermissionModal}>Grant Permission <PlusIcon /></ActionButton>
                </div>
            </div>
            <DataTable table={permissionsTable} columns={permissionColumns} />
        </div>
    }

    const tabHeaders = Object.keys(tabs)

    return <>
        <DepartmentDetailTemplate
            department={department}
            isLoadingDepartment={isLoading}
            content={<Tabs
                headers={tabHeaders}
                tabs={tabs}
                activeTab={tabHeaders.includes(activeTab) ? activeTab : tabHeaders[0]}
                setActiveTab={setActiveTab}
            />}
            handleBack={handleBack}
            handleEdit={canManage ? handleEdit : undefined}
            handleDelete={canDelete ? handleDelete : undefined}
            handleOpenHelpDocument={handleOpenManual}
        />
        <Modal
            isOpen={showMappingModal}
            onClose={handleCancelMappingEdit}
            title={focusedMapping ? "Edit Mapping" : "Add Mapping"}
            subtitle={department?.description}
        >
            <DepartmentMappingForm
                edit={!!focusedMapping}
                sourceTypes={sourceOptions}
                values={mappingFormik.values}
                errors={mappingFormik.errors}
                touched={mappingFormik.touched}
                handleChange={mappingFormik.handleChange}
                handleBlur={mappingFormik.handleBlur}
                handleSubmit={mappingFormik.handleSubmit}
                handleCancel={handleCancelMappingEdit}
                isValid={mappingFormik.isValid}
                isSubmitting={mappingFormik.isSubmitting}
            />
        </Modal>
        <Modal
            isOpen={showPermissionModal}
            onClose={handleClosePermissionModal}
            title="Grant Permission"
            subtitle={department?.description}
        >
            <DepartmentPermissionForm
                employees={employeeOptions}
                permissionTypes={grantTypeOptions}
                values={permissionFormik.values}
                errors={permissionFormik.errors}
                touched={permissionFormik.touched}
                handleChange={permissionFormik.handleChange}
                handleBlur={permissionFormik.handleBlur}
                handleSubmit={permissionFormik.handleSubmit}
                isValid={permissionFormik.isValid}
                isSubmitting={permissionFormik.isSubmitting}
            />
        </Modal>
        <AreYouSureModal {...confirmModal.modalProps} />
    </>
}

export default withRouter(DepartmentDetailsPage)
